import { useState, useMemo } from 'react'

interface StockoutItem {
    itemCardId: number
    description: string
    styleCode: string
    brand: string
    color: string
    size: string
    priorQty: number
    unitsSold60d: number
    revenue60d: number
    /** Stock on hand at another store, VPO Central not counted. */
    availableElsewhere: boolean
    /** Units available to sell at VPO Central (store 90). Null for a snapshot: snapshots don't include Central. */
    centralAts: number | null
    stockoutDate: string
    severity: 'critical' | 'warning'
}

interface Props {
    items: StockoutItem[]
}

type SortKey = 'brand' | 'description' | 'priorQty' | 'unitsSold60d' | 'revenue60d' | 'centralAts' | 'severity'
type SortDir = 'asc' | 'desc'

const SEVERITY_ORDER = { critical: 0, warning: 1 }

/**
 * Themed table panel showing critical stock-out insights with sortable columns.
 * Brand and Description are separate, sortable columns.
 */
export default function StockoutInsightsPanel({ items }: Props) {
    const [sortKey, setSortKey] = useState<SortKey>('revenue60d')
    const [sortDir, setSortDir] = useState<SortDir>('desc')
    const [copied, setCopied] = useState(false)

    const formatCurrency = (n: number) =>
        new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n)

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSortKey(key)
            // Default direction: descending for numbers, ascending for text
            setSortDir(key === 'brand' || key === 'description' ? 'asc' : 'desc')
        }
    }

    const sorted = useMemo(() => {
        const arr = [...items]
        const dir = sortDir === 'asc' ? 1 : -1
        arr.sort((a, b) => {
            switch (sortKey) {
                case 'brand': return dir * a.brand.localeCompare(b.brand)
                case 'description': return dir * a.description.localeCompare(b.description)
                case 'priorQty': return dir * (a.priorQty - b.priorQty)
                case 'unitsSold60d': return dir * (a.unitsSold60d - b.unitsSold60d)
                case 'revenue60d': return dir * (a.revenue60d - b.revenue60d)
                case 'centralAts':
                    // A figure that wasn't recorded goes last either way.
                    if (a.centralAts === null || b.centralAts === null)
                        return (a.centralAts === null ? 1 : 0) - (b.centralAts === null ? 1 : 0)
                    return dir * (a.centralAts - b.centralAts)
                case 'severity': return dir * (SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])
                default: return 0
            }
        })
        return arr
    }, [items, sortKey, sortDir])

    const arrow = (key: SortKey) => {
        if (sortKey !== key) return <span style={{ opacity: 0.3, marginLeft: 4, fontSize: '10px' }}>⇅</span>
        return <span style={{ marginLeft: 4, fontSize: '10px' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
    }

    const thStyle: React.CSSProperties = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }
    const btnStyle: React.CSSProperties = {
        background: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: '4px',
        padding: '4px 8px',
        fontSize: '11px',
        color: 'var(--color-text-secondary)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    }

    const buildExportData = (separator: string) => {
        const headers = ['Brand', 'Description', 'Style Code', 'Variant (Color / Size)', 'Prior Qty', '60d Velocity', 'Est. Lost Rev', 'Other Stores', 'Central ATS', 'Severity']
        const rows = sorted.map(item => [
            item.brand,
            item.description,
            item.styleCode || '',
            item.color && item.size ? `${item.color} / ${item.size}` : item.color || item.size || '',
            item.priorQty.toString(),
            item.unitsSold60d.toString(),
            item.revenue60d.toString(),
            item.availableElsewhere ? 'Yes' : 'No',
            item.centralAts === null ? '' : item.centralAts.toString(),
            item.severity
        ])
        
        return [
            headers.join(separator),
            ...rows.map(row => row.map(v => `"${v.replace(/"/g, '""')}"`).join(separator))
        ].join('\n')
    }

    const handleCopy = async () => {
        const tsv = buildExportData('\t')
        await navigator.clipboard.writeText(tsv)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    const handleExport = () => {
        const csv = buildExportData(',')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.setAttribute('href', url)
        link.setAttribute('download', `critical_stockouts_${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <div className="chart-panel" style={{ height: '100%' }}>
            <div className="chart-panel-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '1rem' }}>
                <span>Critical Stock-Outs</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={handleCopy} style={btnStyle} title="Copy to Clipboard">
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                    <button onClick={handleExport} style={btnStyle} title="Export to CSV">
                        ⬇️ Export
                    </button>
                </div>
            </div>
            {items.length === 0
                ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-tertiary)' }}>
                        No critical stock-outs detected in the latest snapshot
                    </div>
                )
                : (
                    <div className="chart-panel-content">
                        <table className="top-sellers-table">
                            <thead>
                                <tr>
                                    <th style={thStyle} onClick={() => handleSort('brand')}>Brand{arrow('brand')}</th>
                                    <th style={thStyle} onClick={() => handleSort('description')}>Description{arrow('description')}</th>
                                    <th>Variant</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('priorQty')}>Prior Qty{arrow('priorQty')}</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('unitsSold60d')}>60d Velocity{arrow('unitsSold60d')}</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('revenue60d')}>Est. Lost Rev{arrow('revenue60d')}</th>
                                    <th style={{ textAlign: 'center' }} title="Stock on hand at another store. VPO Central is counted separately.">Other Stores</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => handleSort('centralAts')}
                                        title="Units available to sell at VPO Central (store 90). Live only: the monthly snapshots don't include Central.">
                                        Central ATS{arrow('centralAts')}
                                    </th>
                                    <th style={{ ...thStyle, textAlign: 'center' }} onClick={() => handleSort('severity')}>Severity{arrow('severity')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((item, i) => (
                                    <tr key={i}>
                                        <td>
                                            <div className="brand">{item.brand}</div>
                                        </td>
                                        <td>
                                            <div>{item.description}</div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                                                {item.styleCode}
                                            </div>
                                        </td>
                                        <td>
                                            {item.color && item.size
                                                ? `${item.color} / ${item.size}`
                                                : item.color || item.size || '—'}
                                        </td>
                                        <td className="qty">{item.priorQty.toLocaleString()}</td>
                                        <td className="qty">{item.unitsSold60d.toLocaleString()} units</td>
                                        <td className="revenue">{formatCurrency(item.revenue60d)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {item.availableElsewhere
                                                ? <span style={{ color: 'var(--color-success)' }}>✓ Yes</span>
                                                : <span style={{ color: 'var(--color-error)' }}>✕ No</span>}
                                        </td>
                                        <td className="qty">{item.centralAts === null ? '—' : item.centralAts.toLocaleString()}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: 'var(--text-xs)',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                                background: item.severity === 'critical'
                                                    ? 'rgba(239, 68, 68, 0.15)'
                                                    : 'rgba(245, 158, 11, 0.15)',
                                                color: item.severity === 'critical'
                                                    ? 'var(--color-error)'
                                                    : 'var(--color-warning)',
                                            }}>
                                                {item.severity}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
        </div>
    )
}
